// ════════════════════════════════════════════════════════════════════
// LYRAEA — data layer
// ────────────────────────────────────────────────────────────────────
// THE ONE DRAWER: everything Lyraea owns lives under games/lyraea/…,
// reached through ONE BASE constant. No new top-level node, ever. The
// `games` umbrella is already open to authed users, so this whole title
// stands up with ZERO rules deploys during beta — which is exactly why
// the umbrella exists.
//
// THE SHARED SOUL is not copied here. A listener's identity, Crests,
// deeds and Mark stay at profiles/{uid}/… where every title reads them.
// Lyraea writes only its own per-game records lane.
//
// ── WHAT LYRAEA DELIBERATELY CANNOT DO YET ───────────────────────────
// It cannot spend a Divine Crest. Not a locked function, not a disabled
// button — there is no spend path in this file at all. A priced track
// shows its price and refuses to sell, because the currency write-trust
// gap (TERROR_CURRENCY_SECURITY.md) is still open: rules validate a
// currency as "a non-negative number" and nothing more, so any client
// could address the field directly. Vending lands AFTER the hardening
// pass, server-side, and never before. Shipping a shop on top of an
// unhardened currency is how you find out your economy was fiction.
//
// ── CURRENCY ─────────────────────────────────────────────────────────
// Lyraea mints NOTHING. It uses ◆ Divine Crests only, per caP'n: this
// app does not earn currency, it spends it. That keeps it outside the
// two-currency question entirely — there is no Lyraea coin to register,
// no rate row, no Crest Well entry. One less thing that can inflate.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from "react";
import { ref, get, set, update, remove, onValue, off, increment } from "firebase/database";
import { db, auth } from "./firebase";

export const GAME_ID = "lyraea";
export const BASE = `games/${GAME_ID}`;          // ⟵ THE one path constant

// Every path in the title derives from BASE. Never concatenate elsewhere.
export const tracksPath  = () => `${BASE}/tracks`;
export const trackPath   = (id) => `${BASE}/tracks/${id}`;
export const albumsPath  = () => `${BASE}/albums`;
export const albumPath   = (id) => `${BASE}/albums/${id}`;
// Per-game records lane on the shared profile. Ours to write, per the
// write-lane table: profiles/{uid}/records/{gameId}/…
export const myLanePath  = (uid) => `profiles/${uid}/records/${GAME_ID}`;
export const likesPath   = (uid) => `${myLanePath(uid)}/liked`;

export const KINDS = ["track", "album"];

// ════════════════════════════════════════════════════════════════════
// THE TWO SHAPES A NON-TRACK THING CAN HAVE  (2026-08-13)
// ────────────────────────────────────────────────────────────────────
// caP'n drew this line and it is the whole reason the schema does not
// blur: the same person, uploading the same painting, produces TWO
// DIFFERENT THINGS depending on what it is FOR.
//
//   AN EXTRA is PART OF A RELEASE. Lyrics, liner notes, the bonus art
//   that ships with the album. It has no price, no shelf and no life of
//   its own — it arrives when you get the release and it leaves when the
//   release does. `games/lyraea/extras/{id}`, pointing at a track or an
//   album.
//
//   AN ITEM IS ITS OWN RELEASE. The painting they made on Tuesday. It
//   sits on the shelf, it has a price, it can be acquired by itself.
//   `games/lyraea/items/{id}`, pointing at nothing.
//
// ⚠ WHY A TRACK IS NOT JUST AN ITEM OF KIND "track", tempting as that
//   is. It very nearly should be — nothing in this schema has ever said
//   "song", on purpose. But the audio engine, play counts, albums, sets
//   and the whole player key off `games/lyraea/tracks`, and folding them
//   into one node buys a tidier diagram at the cost of a live schema
//   break with zero player-visible gain. So: tracks keep their node,
//   items take the rest, and `shelfItems()` below unifies them for every
//   surface that should not care. If that ever stops being true, the
//   unifier is the one place that changes. (THE EXCEPTION LAW.)
export const EXTRA_KINDS = ["lyrics", "notes", "art"];
export const ITEM_KINDS  = ["art", "writing", "book", "photo"];

// What each kind needs, and how it is shown. `body` kinds store their
// content inline in Firebase (text is small); `src` kinds store a
// FILENAME on the asset CDN, exactly like a track — never a URL, so the
// whole library still moves hosts by editing one line in CDN.js.
export const KIND_META = {
  lyrics:  { label: "Lyrics",      glyph: "♪", holds: "body", blurb: "The words, as sung." },
  notes:   { label: "Liner Notes", glyph: "✎", holds: "body", blurb: "Whatever you want to say about it." },
  art:     { label: "Art",         glyph: "◈", holds: "src",  blurb: "A picture." },
  writing: { label: "Writing",     glyph: "✒", holds: "body", blurb: "A poem, an essay, a letter." },
  book:    { label: "Book",        glyph: "▤", holds: "src",  blurb: "A file to keep." },
  photo:   { label: "Photo",       glyph: "❐", holds: "src",  blurb: "A photograph." },
  track:   { label: "Track",       glyph: "▸", holds: "src",  blurb: "Music." },
};

export const holdsBody = (kind) => KIND_META[kind]?.holds === "body";

export const extrasPath = () => `${BASE}/extras`;
export const extraPath  = (id) => `${BASE}/extras/${id}`;
export const itemsPath  = () => `${BASE}/items`;
export const itemPath   = (id) => `${BASE}/items/${id}`;
export const ownedPath  = (uid) => `${myLanePath(uid)}/owned`;

// ════════════════════════════════════════════════════════════════════
// THE TRACK SHAPE
// ════════════════════════════════════════════════════════════════════
// `src` and `cover` are FILENAMES on the asset CDN, not URLs — resolved
// through CDN.js at render time. Storing bare filenames is what lets the
// whole library move hosts by editing one line when uploads open up.
//
// `priceDC` is display-only today (see the note at the top of this file).
// It is stored now so that nothing has to be migrated later: when vending
// lands, the server re-reads THIS number and settles against it — never
// a price a buyer's client happened to be holding.
export const EMPTY_TRACK = {
  id: "",
  title: "Untitled",
  artist: "",
  artistUid: "",          // whose Mark this belongs to
  albumId: "",
  src: "",                // filename on lyraea-assets
  cover: "",              // filename, or a full URL to art elsewhere in the Empire
  durationSec: 0,
  priceDC: 0,             // 0 = free. Display-only until vending lands.
  releasedAt: 0,
  order: 0,               // position within its album
  plays: 0,
};

const clean = (t, id) => ({
  ...EMPTY_TRACK,
  ...t,
  id: t?.id || id,
  priceDC: Math.max(0, Math.floor(Number(t?.priceDC) || 0)),
  durationSec: Math.max(0, Math.floor(Number(t?.durationSec) || 0)),
});

// ════════════════════════════════════════════════════════════════════
// READ
// ════════════════════════════════════════════════════════════════════

// The whole catalogue, live. Small by design at this stage: one listener,
// one payload, everything downstream filters in memory. When the library
// outgrows that this becomes a paged query and no screen changes, because
// every screen already reads through this hook.
export function useCatalogue() {
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const tRef = ref(db, tracksPath());
    const cb = onValue(tRef, (snap) => {
      const v = snap.val() || {};
      setTracks(
        Object.entries(v)
          .map(([id, t]) => clean(t, id))
          .sort((a, b) => (b.releasedAt || 0) - (a.releasedAt || 0))
      );
      setLoading(false);
    }, (e) => { setError(e); setLoading(false); });
    return () => off(tRef, "value", cb);
  }, []);

  useEffect(() => {
    const aRef = ref(db, albumsPath());
    const cb = onValue(aRef, (snap) => setAlbums(snap.val() || {}), () => {});
    return () => off(aRef, "value", cb);
  }, []);

  return { tracks, albums, loading, error };
}

// A listener's own lane — likes, play counts, last played.
export function useMyLane(uid) {
  const [lane, setLane] = useState({});
  useEffect(() => {
    if (!uid) { setLane({}); return; }
    const r = ref(db, myLanePath(uid));
    const cb = onValue(r, (snap) => setLane(snap.val() || {}), () => {});
    return () => off(r, "value", cb);
  }, [uid]);

  return useMemo(() => ({
    liked: lane.liked || {},
    plays: lane.plays || {},
    lastTrackId: lane.lastTrackId || null,
    isLiked: (id) => !!(lane.liked || {})[id],
  }), [lane]);
}

// ════════════════════════════════════════════════════════════════════
// WRITE — listener side. Nothing here can touch a currency.
// ════════════════════════════════════════════════════════════════════
const me = () => auth.currentUser?.uid || null;

const assertOwner = (uid) => {
  const u = me();
  if (!u) throw new Error("[Lyraea] not signed in");
  if (u !== uid) throw new Error("[Lyraea] refusing to write another listener's lane");
  return u;
};

export const toggleLike = async (uid, trackId, liked) => {
  assertOwner(uid);
  await set(ref(db, `${likesPath(uid)}/${trackId}`), liked ? Date.now() : null);
};

// A play is counted once the track has genuinely been listened to (the
// caller decides the threshold — see audio.js, which fires at 20s or
// halfway, whichever is sooner). Two atomic increments, never a
// read-modify-write: two plays landing at once must not eat each other.
export const countPlay = async (uid, trackId) => {
  try {
    await update(ref(db, trackPath(trackId)), { plays: increment(1) });
    if (uid && me() === uid) {
      await update(ref(db, myLanePath(uid)), {
        [`plays/${trackId}`]: increment(1),
        lastTrackId: trackId,
        lastPlayedAt: Date.now(),
      });
    }
  } catch {
    // A play count is never worth breaking playback over. Swallow it.
  }
};

// ════════════════════════════════════════════════════════════════════
// WRITE — artist side. Publishing.
// ════════════════════════════════════════════════════════════════════
// v1 is caP'n's own catalogue: publishing is open to any authed user
// under the games umbrella, and the ONLY guard is that you cannot
// publish under someone else's uid or edit a track you did not upload.
// That guard is here in code today; when the library opens to other
// people it moves into rules as a real per-track owner seal. Written
// down so nobody later assumes the rules are already doing it.
export const publishTrack = async (uid, track) => {
  assertOwner(uid);
  const id = track.id || `tr_${Date.now().toString(36)}`;
  const existing = (await get(ref(db, trackPath(id)))).val();
  if (existing && existing.artistUid && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that track belongs to another Mark");
  }
  const row = clean({
    ...track,
    id,
    artistUid: uid,
    releasedAt: track.releasedAt || existing?.releasedAt || Date.now(),
    plays: existing?.plays || 0,
  }, id);
  await set(ref(db, trackPath(id)), row);
  return row;
};

// Self-healing duration. Tracks published before the metadata probe
// existed store 0, which renders as a 0:00 length forever. The engine
// learns the real number the first time the artist plays their own track,
// so it writes it back — ONE field, owner-only, never touching plays or
// anything else on the row. Listeners cannot trigger this: it is guarded
// on artistUid, so a stranger's playback can never edit a catalogue row.
export const setTrackDuration = async (uid, trackId, secs) => {
  assertOwner(uid);
  const s = Math.round(Number(secs) || 0);
  if (!s) return;
  const existing = (await get(ref(db, trackPath(trackId)))).val();
  if (!existing || existing.artistUid !== uid) return;
  if (existing.durationSec) return;              // never overwrite a real value
  await update(ref(db, trackPath(trackId)), { durationSec: s });
};

export const unpublishTrack = async (uid, trackId) => {
  assertOwner(uid);
  const existing = (await get(ref(db, trackPath(trackId)))).val();
  if (existing && existing.artistUid && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that track belongs to another Mark");
  }
  await remove(ref(db, trackPath(trackId)));
};

// ════════════════════════════════════════════════════════════════════
// ALBUMS
// ════════════════════════════════════════════════════════════════════
// An album is a HEADER, not a container. It holds a title, a cover and a
// credit — and no track list at all. Membership lives on the track
// (`albumId` + `order`), which is the opposite of how a set works, and
// deliberately so:
//
//   • A track belongs to exactly ONE album, so the fact fits naturally on
//     the track. A track belongs to MANY sets, so that fact cannot.
//   • The artist owns both sides of the relationship — the album row and
//     the track row are theirs — so a single writer, no contention.
//   • Nothing can disagree with itself. There is no list to fall out of
//     step with reality: delete a track and it leaves its album by
//     definition, because the only record of membership went with it.
//
// A dangling `albumId` (album deleted, track kept) is harmless: the track
// simply resolves to no album and keeps behaving like a single.
export const saveAlbum = async (uid, album) => {
  assertOwner(uid);
  const id = album.id || `al_${Date.now().toString(36)}`;
  const existing = (await get(ref(db, albumPath(id)))).val();
  if (existing && existing.artistUid && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that album belongs to another Mark");
  }
  await set(ref(db, albumPath(id)), {
    id,
    title: String(album.title || "Untitled").slice(0, 90),
    artist: String(album.artist || "").slice(0, 90),
    artistUid: uid,
    cover: album.cover || "",
    releasedAt: album.releasedAt || existing?.releasedAt || Date.now(),
  });
  return id;
};

// Deleting an album releases its tracks rather than orphaning them —
// they become singles again, which is the only non-destructive reading of
// "delete the album but keep the music."
export const deleteAlbum = async (uid, albumId, tracks = []) => {
  assertOwner(uid);
  const existing = (await get(ref(db, albumPath(albumId)))).val();
  if (existing && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that album belongs to another Mark");
  }
  const mine = tracks.filter((t) => t.albumId === albumId && t.artistUid === uid);
  await Promise.all(mine.map((t) =>
    update(ref(db, trackPath(t.id)), { albumId: "", order: 0 })));
  await remove(ref(db, albumPath(albumId)));
};

// Put a track on an album (or take it off, with albumId = ""). Two fields,
// nothing else on the row touched — plays and price are untouchable here.
export const setTrackAlbum = async (uid, trackId, albumId, order = 0) => {
  assertOwner(uid);
  const existing = (await get(ref(db, trackPath(trackId)))).val();
  if (!existing || existing.artistUid !== uid) {
    throw new Error("[Lyraea] that track belongs to another Mark");
  }
  await update(ref(db, trackPath(trackId)), {
    albumId: albumId || "",
    order: Math.max(0, Math.floor(Number(order) || 0)),
  });
};

export function useAlbums() {
  const [albums, setAlbums] = useState({});
  useEffect(() => {
    const r = ref(db, albumsPath());
    const cb = onValue(r, (snap) => setAlbums(snap.val() || {}), () => {});
    return () => off(r, "value", cb);
  }, []);

  return useMemo(() => {
    const list = Object.entries(albums)
      .map(([id, a]) => ({ ...a, id }))
      .sort((x, y) => (y.releasedAt || 0) - (x.releasedAt || 0));
    return { list, byId: (id) => list.find((a) => a.id === id) || null };
  }, [albums]);
}

// The track list of an album, in the artist's chosen running order.
// Ties fall back to title so an artist who never set an order still gets
// something stable rather than whatever Firebase felt like returning.
export const albumTracks = (albumId, tracks) =>
  tracks
    .filter((t) => t.albumId === albumId)
    .sort((a, b) => (a.order || 0) - (b.order || 0) ||
                    String(a.title).localeCompare(String(b.title)));

// ════════════════════════════════════════════════════════════════════
// SETS — what everyone else calls playlists
// ════════════════════════════════════════════════════════════════════
// A set lives in the listener's own lane: profiles/{uid}/records/lyraea/
// sets/{setId}. It is theirs, it follows them across devices, and it costs
// the catalogue nothing.
//
// THE MEMBERSHIP IS A MAP, NOT AN ARRAY. `tracks: { trackId: order }`.
// An array would mean read-modify-write on every add — two people (or two
// tabs) adding at once and one add vanishes. A keyed child write is atomic
// and cannot lose a neighbour, which is the same reasoning that keeps
// currency on delta transactions. Order is a number, so reordering later
// is a write per moved row rather than a rewrite of the list.
//
// A set stores TRACK IDS ONLY — never copies of tracks. Retitle a track in
// the Studio and every set holding it updates for free, because there was
// never a second copy to go stale.
export const setsPath = (uid) => `${myLanePath(uid)}/sets`;
export const setPath  = (uid, setId) => `${setsPath(uid)}/${setId}`;

export function useSets(uid) {
  const [sets, setSets] = useState({});
  useEffect(() => {
    if (!uid) { setSets({}); return; }
    const r = ref(db, setsPath(uid));
    const cb = onValue(r, (snap) => setSets(snap.val() || {}), () => {});
    return () => off(r, "value", cb);
  }, [uid]);

  return useMemo(() => {
    const list = Object.entries(sets).map(([id, s]) => ({
      id,
      name: s?.name || "Untitled set",
      createdAt: s?.createdAt || 0,
      tracks: s?.tracks || {},
      count: Object.keys(s?.tracks || {}).length,
    })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    return { list, byId: (id) => list.find((s) => s.id === id) || null };
  }, [sets]);
}

export const createSet = async (uid, name) => {
  assertOwner(uid);
  const id = `set_${Date.now().toString(36)}`;
  await set(ref(db, setPath(uid, id)), {
    id, name: String(name || "Untitled set").slice(0, 60),
    createdAt: Date.now(), tracks: {},
  });
  return id;
};

export const renameSet = async (uid, setId, name) => {
  assertOwner(uid);
  await update(ref(db, setPath(uid, setId)), { name: String(name || "").slice(0, 60) });
};

export const deleteSet = async (uid, setId) => {
  assertOwner(uid);
  await remove(ref(db, setPath(uid, setId)));
};

// Order is a timestamp: monotonic, never collides, and appends to the end
// without having to know how long the set already is.
export const addToSet = async (uid, setId, trackId) => {
  assertOwner(uid);
  await set(ref(db, `${setPath(uid, setId)}/tracks/${trackId}`), Date.now());
};

export const removeFromSet = async (uid, setId, trackId) => {
  assertOwner(uid);
  await set(ref(db, `${setPath(uid, setId)}/tracks/${trackId}`), null);
};

// Resolve a set's ids against the live catalogue, in order. Ids that no
// longer exist (the artist pulled the track) simply drop out — a set can
// never render a ghost row, and it does not need cleaning up to stay valid.
export const resolveSet = (setObj, tracks) => {
  if (!setObj) return [];
  const byId = new Map(tracks.map((t) => [t.id, t]));
  return Object.entries(setObj.tracks || {})
    .sort((a, b) => a[1] - b[1])
    .map(([id]) => byId.get(id))
    .filter(Boolean);
};

// ════════════════════════════════════════════════════════════════════
// EXTRAS — the things that come WITH a release
// ════════════════════════════════════════════════════════════════════
// An extra points at exactly one parent: `trackId` OR `albumId`, never
// both and never neither. The parent pointer lives on the EXTRA, which
// is the same choice albums made and for the same reason — the release
// has no list to fall out of step with, so nothing can disagree with
// itself, and deleting a release cannot leave a half-truth behind.
//
// ⚠ AN ORPHANED EXTRA IS INVISIBLE, NOT BROKEN. Delete a track and its
//   lyrics resolve to no parent and simply stop being rendered anywhere.
//   They are not chased down and deleted, because a delete that fans out
//   is a delete that can half-fail, and lyrics costing three bytes are
//   not worth a cascade. `sweepOrphanExtras()` exists for when the
//   artist actually wants them gone.

export const EMPTY_EXTRA = {
  id: "", kind: "lyrics", title: "",
  trackId: "", albumId: "",
  body: "", src: "",
  artistUid: "", order: 0, createdAt: 0,
};

const cleanExtra = (e, id) => ({
  ...EMPTY_EXTRA, ...e,
  id: e?.id || id,
  kind: EXTRA_KINDS.includes(e?.kind) ? e.kind : "notes",
  order: Math.max(0, Math.floor(Number(e?.order) || 0)),
});

export function useExtras() {
  const [extras, setExtras] = useState([]);
  useEffect(() => {
    const r = ref(db, extrasPath());
    const cb = onValue(r, (snap) => {
      const v = snap.val() || {};
      setExtras(Object.entries(v)
        .map(([id, e]) => cleanExtra(e, id))
        .sort((a, b) => (a.order || 0) - (b.order || 0) || (a.createdAt || 0) - (b.createdAt || 0)));
    }, () => {});
    return () => off(r, "value", cb);
  }, []);
  return extras;
}

// Everything attached to one release. A track ALSO inherits its album's
// extras — liner notes belong to the record, and a listener on track 4
// should not have to go looking for them. Track-level comes first
// because it is the more specific thing.
export const extrasFor = (extras, { trackId, albumId } = {}) => {
  const own = extras.filter((e) => (trackId && e.trackId === trackId) || (albumId && e.albumId === albumId));
  if (!trackId || !albumId) return own;
  return [
    ...own.filter((e) => e.trackId === trackId),
    ...own.filter((e) => e.albumId === albumId && e.trackId !== trackId),
  ];
};

export const lyricsFor = (extras, trackId) =>
  extras.find((e) => e.kind === "lyrics" && e.trackId === trackId) || null;

export const saveExtra = async (uid, extra) => {
  assertOwner(uid);
  if (!extra?.trackId && !extra?.albumId) {
    throw new Error("[Lyraea] an extra belongs to a release — give it a track or an album, or publish it as an item instead");
  }
  if (extra.trackId && extra.albumId) {
    throw new Error("[Lyraea] an extra has ONE parent. Attach it to the track or to the album, not both.");
  }
  const id = extra.id || `ex_${Date.now().toString(36)}`;
  const existing = (await get(ref(db, extraPath(id)))).val();
  if (existing && existing.artistUid && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that extra belongs to another Mark");
  }
  const row = cleanExtra({
    ...extra, id, artistUid: uid,
    title: String(extra.title || KIND_META[extra.kind]?.label || "Extra").slice(0, 90),
    body: holdsBody(extra.kind) ? String(extra.body || "").slice(0, 20000) : "",
    src:  holdsBody(extra.kind) ? "" : String(extra.src || ""),
    createdAt: existing?.createdAt || Date.now(),
  }, id);
  await set(ref(db, extraPath(id)), row);
  return row;
};

export const deleteExtra = async (uid, extraId) => {
  assertOwner(uid);
  const existing = (await get(ref(db, extraPath(extraId)))).val();
  if (existing && existing.artistUid && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that extra belongs to another Mark");
  }
  await remove(ref(db, extraPath(extraId)));
};

// Extras whose release no longer exists. Offered to the artist as a
// tidy-up, never run automatically — an automatic sweep on a live
// catalogue is one bad read away from deleting somebody's lyrics.
export const orphanExtras = (extras, tracks, albums) => {
  const t = new Set(tracks.map((x) => x.id));
  const a = new Set(Object.keys(albums || {}));
  return extras.filter((e) =>
    (e.trackId && !t.has(e.trackId)) || (e.albumId && !a.has(e.albumId)));
};

export const sweepOrphanExtras = async (uid, orphans = []) => {
  assertOwner(uid);
  const mine = orphans.filter((e) => e.artistUid === uid);
  await Promise.all(mine.map((e) => remove(ref(db, extraPath(e.id)))));
  return mine.length;
};

// ════════════════════════════════════════════════════════════════════
// ITEMS — the everything-platform, one node at a time
// ════════════════════════════════════════════════════════════════════
// Same shape language as a track on purpose: an id, a title, a Mark, a
// filename or a body, a cover, a price, a release date. A surface that
// can render a track row can render one of these with almost no work,
// which is what stops "and now we support books" from being a rewrite.
//
// ⚠ A VIEWER IS NOT FREE, and that is the real cost of this node. The
//   schema never said "song" and that was the cheap half. Every kind
//   still needs somewhere to LOOK at it, and a kind with no viewer is a
//   row you can own and cannot open. `KIND_META.holds` is what tells a
//   viewer which of the two things it is dealing with, so a new kind is
//   a row in that table plus one branch — not a new subsystem.

export const EMPTY_ITEM = {
  id: "", kind: "art", title: "Untitled",
  artist: "", artistUid: "",
  src: "", body: "", cover: "",
  priceDC: 0, releasedAt: 0, views: 0,
};

const cleanItem = (i, id) => ({
  ...EMPTY_ITEM, ...i,
  id: i?.id || id,
  kind: ITEM_KINDS.includes(i?.kind) ? i.kind : "art",
  priceDC: Math.max(0, Math.floor(Number(i?.priceDC) || 0)),
});

export function useItems() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const r = ref(db, itemsPath());
    const cb = onValue(r, (snap) => {
      const v = snap.val() || {};
      setItems(Object.entries(v)
        .map(([id, i]) => cleanItem(i, id))
        .sort((a, b) => (b.releasedAt || 0) - (a.releasedAt || 0)));
    }, () => {});
    return () => off(r, "value", cb);
  }, []);
  return items;
}

export const publishItem = async (uid, item) => {
  assertOwner(uid);
  const id = item.id || `it_${Date.now().toString(36)}`;
  const existing = (await get(ref(db, itemPath(id)))).val();
  if (existing && existing.artistUid && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that item belongs to another Mark");
  }
  const row = cleanItem({
    ...item, id, artistUid: uid,
    title: String(item.title || "Untitled").slice(0, 90),
    body: holdsBody(item.kind) ? String(item.body || "").slice(0, 200000) : "",
    src:  holdsBody(item.kind) ? "" : String(item.src || ""),
    releasedAt: item.releasedAt || existing?.releasedAt || Date.now(),
    views: existing?.views || 0,
  }, id);
  await set(ref(db, itemPath(id)), row);
  return row;
};

export const unpublishItem = async (uid, itemId) => {
  assertOwner(uid);
  const existing = (await get(ref(db, itemPath(itemId)))).val();
  if (existing && existing.artistUid && existing.artistUid !== uid) {
    throw new Error("[Lyraea] that item belongs to another Mark");
  }
  await remove(ref(db, itemPath(itemId)));
};

// One shelf, both nodes. Every surface that should not care which node a
// thing came out of reads through here — and `kind` is what it sorts on,
// not which array it arrived in.
export const shelfItems = (tracks = [], items = []) => [
  ...tracks.map((t) => ({ ...t, kind: "track" })),
  ...items,
].sort((a, b) => (b.releasedAt || 0) - (a.releasedAt || 0));

// ════════════════════════════════════════════════════════════════════
// ⟡ VENDING — THE FREE SHELF   (caP'n, 2026-08-13)
// ════════════════════════════════════════════════════════════════════
// LYRAEA_PRIME Law 4 used to read "prices are shown, never charged" and
// there was no acquisition path in this file at all. There is one now,
// and the law it replaces reads:
//
//        ⟡ THE SHELF OPENS AT ZERO. ⟡
//        A thing priced at ◆0 can be acquired. A thing priced at
//        anything else cannot, and the code to charge for it does
//        not exist.
//
// ── WHY THIS IS SAFE WHEN A REAL STOREFRONT IS NOT ──────────────────
// The blocker was never "shops are hard." It was that ◆ is not yet
// write-trusted: Firebase rules validate a currency as a non-negative
// number and nothing more, so any authed player can address the field
// directly and set it to whatever they like. A shop built on that reads
// a balance it cannot believe.
//
// A FREE SHELF NEVER READS A BALANCE. That is the entire argument, and
// it is why this is not a loophole in Law 4 but the largest piece of it
// that was always safe to ship. `acquire()` below:
//
//   • reads `priceDC` FRESH FROM THE CATALOGUE, never from the caller —
//     a client passing its own price is exactly the attack shape
//   • refuses anything above 0, loudly
//   • writes ONE key into the listener's own lane
//   • never reads, writes, decrements or even imports a currency
//
// There is no currency field named anywhere in this file — not read,
// not written, not imported — and that absence IS the safety property.
// Preserve it. **The day a price above zero settles
// here, Layer Two must already be deployed** — server authority,
// `mass app/ie-functions/`, the Blaze plan. Not the clamp. Not "soon."
// See gamemaster/IE_CURRENCY_HARDENING.md §3.

export const PRICED_NOTICE =
  "Priced items open when the Empire's currency is server-authoritative. Free things work today.";

export function useOwned(uid) {
  const [owned, setOwned] = useState({});
  useEffect(() => {
    if (!uid) { setOwned({}); return; }
    const r = ref(db, ownedPath(uid));
    const cb = onValue(r, (snap) => setOwned(snap.val() || {}), () => {});
    return () => off(r, "value", cb);
  }, [uid]);
  return useMemo(() => ({
    map: owned,
    owns: (id) => !!owned[id],
    count: Object.keys(owned).length,
  }), [owned]);
}

// Acquire a free thing. Returns { ok, reason } and never throws on the
// ordinary refusal, because "this one costs something" is an answer the
// UI shows, not an error it catches.
export const acquire = async (uid, id, kind = "item") => {
  try {
    assertOwner(uid);
  } catch {
    return { ok: false, reason: "Sign in to keep things." };
  }
  if (!id) return { ok: false, reason: "Nothing to acquire." };

  // ⚠ THE PRICE COMES FROM THE CATALOGUE, NOT FROM THE CALLER. A caller
  //   that hands us a price is describing what it WANTS to pay, and the
  //   whole class of storefront bug lives in believing it.
  const node = kind === "track" ? trackPath(id) : itemPath(id);
  const row = (await get(ref(db, node))).val();
  if (!row) return { ok: false, reason: "That is no longer on the shelf." };

  const price = Math.max(0, Math.floor(Number(row.priceDC) || 0));
  if (price > 0) {
    return { ok: false, reason: PRICED_NOTICE, priced: true, priceDC: price };
  }

  await set(ref(db, `${ownedPath(uid)}/${id}`), {
    at: Date.now(),
    kind: kind === "track" ? "track" : (row.kind || "item"),
    // Stamped so a later audit can prove every acquisition settled at
    // zero, which is the record that makes reopening this safe.
    paidDC: 0,
  });
  return { ok: true, free: true };
};

export const release = async (uid, id) => {
  assertOwner(uid);
  await set(ref(db, `${ownedPath(uid)}/${id}`), null);
};

// ════════════════════════════════════════════════════════════════════
// METADATA PROBE — read a file's real length instead of asking for it
// ════════════════════════════════════════════════════════════════════
// Typing a track's duration by hand is a small indignity that also gets it
// wrong. The browser already knows: point an Audio element at the URL,
// wait for `loadedmetadata`, read `duration`, throw the element away. No
// download of the whole file — `preload="metadata"` fetches only the
// header. Resolves null on failure so publishing never blocks on it.
export const probeDuration = (url, timeoutMs = 8000) =>
  new Promise((resolve) => {
    if (!url || typeof window === "undefined") return resolve(null);
    const a = new Audio();
    let done = false;
    const finish = (v) => {
      if (done) return;
      done = true;
      a.src = "";
      resolve(v);
    };
    a.preload = "metadata";
    a.addEventListener("loadedmetadata", () =>
      finish(isFinite(a.duration) ? Math.round(a.duration) : null));
    a.addEventListener("error", () => finish(null));
    setTimeout(() => finish(null), timeoutMs);
    a.src = url;
  });

// ── Formatting ───────────────────────────────────────────────────────
export const fmtTime = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

export const fmtPlays = (n) => {
  const v = Number(n || 0);
  if (v < 1000) return String(v);
  if (v < 1e6) return `${(v / 1000).toFixed(v < 10000 ? 1 : 0)}K`;
  return `${(v / 1e6).toFixed(1)}M`;
};
